import { Component, DestroyRef, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { BenefitsUiAuthSerive } from '@roosevelt/benefits-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { AuthorizationsAsync } from '../authorizations/authorizations-async';
import { getRelaxedUiAuth } from '../authorizations/selector';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import { createFeatureManagedClientSearchController, provideClientSearchStatePrefix } from '../shared/client-search-render.controller';

const BENEFITS_INQUIRY_CLIENT_SEARCH_PREFIX = 'benefits-inquiry-';

@Component({
  selector: 'app-benefits-inquiry',
  templateUrl: './benefits-inquiry.component.html',
  styleUrls: ['./benefits-inquiry.component.scss'],
  providers: [provideClientSearchStatePrefix(BENEFITS_INQUIRY_CLIENT_SEARCH_PREFIX)],
  standalone: false
})
export class BenefitsInquiryComponent implements OnInit, OnDestroy {
  readonly luxId = 'BMT-BenefitsInquiry-Comp' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly relaxedAuth = toSignal(this.lux.get(getRelaxedUiAuth, this.luxId), { initialValue: {} as IUiAuthorizations | {} });
  readonly clientSearch = createFeatureManagedClientSearchController({
    lux: this.lux,
    storagePrefix: BENEFITS_INQUIRY_CLIENT_SEARCH_PREFIX,
    selectedAdminPlan: this.selectedAdminPlan,
    remountTrigger: this.selectedAdminPlan,
    isActive: () => this.initialized,
  });
  readonly selectedSubClients = signal<any[]>([]);
  readonly feed = signal({
    planAcronym: '',
    clientSpecifiedId: '',
    subClientSpecifiedId: '',
    asOf: ''
  });
  private initialized = false;

  constructor(
    private readonly lux: Lux<{ login: ILoginState, appUiAuth: IAppAuthorizations }>,
    private readonly uiAuthAsync: AuthorizationsAsync,
    private readonly benefitsUiAuth: BenefitsUiAuthSerive
  ) {
    effect(() => {
      const uiAuth = this.relaxedAuth() as IUiAuthorizations;
      if (uiAuth?.benefitsInquiry) {
        this.benefitsUiAuth.setRelaxedAuthorizations(uiAuth.benefitsInquiry);
      }
    });
  }

  ngOnInit(): void {
    this.initialized = true;
    this.clientSearch.scheduleRender(true);
  }

  resetOrClientSelected(_event?: unknown): void {
    this.selectedSubClients.set([]);
    this.feed.set({
      planAcronym: '',
      clientSpecifiedId: '',
      subClientSpecifiedId: '',
      asOf: ''
    });
  }

  getSubClientDetails(clientInfo: any[]): void {
    if (!clientInfo?.length) {
      return;
    }

    const selectedClient = clientInfo[0];
    const appContext = `${this.selectedAdminPlan()}/${selectedClient.planAcronym}/${selectedClient.client.specifiedId}/${selectedClient.specifiedId}`;
    this.uiAuthAsync.getAuthorizations('strict', appContext)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(uiAuth => {
        this.selectedSubClients.set(clientInfo);
        this.feed.set({
          planAcronym: selectedClient.planAcronym,
          clientSpecifiedId: selectedClient.client.specifiedId,
          subClientSpecifiedId: selectedClient.specifiedId,
          asOf: moment(selectedClient.asOfDate).format('YYYY-MM-DD')
        });
        if (this.hasBenefitsInquiry(uiAuth)) {
          this.benefitsUiAuth.setStrictAuthorizations(uiAuth.benefitsInquiry);
        }
      });
  }

  private hasBenefitsInquiry(value: unknown): value is IUiAuthorizations & { benefitsInquiry: NonNullable<IUiAuthorizations['benefitsInquiry']> } {
    return !!value && typeof value === 'object' && 'benefitsInquiry' in value && !!(value as IUiAuthorizations).benefitsInquiry;
  }


  ngOnDestroy(): void {
    this.clientSearch.destroy();
    this.lux.destroy(this.luxId);
  }
}
